import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import emailjs from "@emailjs/browser";
import Header from "../../components/Customer/Header";
import Footer from "../../components/Customer/Footer";
import { getDestinationImage } from "../../constants/destinations";
import { VEHICLES } from "../../constants/vehicles";

const EMAILJS_SERVICE_ID = "service_x9hsiqe";
const EMAILJS_TEMPLATE_ID = "template_fw4nx9u";
const EMAILJS_PUBLIC_KEY = "xl6o2FbDrP_sIatDz";

function DayTripBookingPage() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    destination: "",
    destinationName: "",
    numberOfGuests: 1,
    vehiclePreference: "",
    date: "",
    additionalNotes: "",
    customerName: "",
    email: "",
    phone: ""
  });

  useEffect(() => {
    const destination = searchParams.get("destination");
    const destinationName = searchParams.get("name");
    if (destination && destinationName) {
      setFormData(prev => ({
        ...prev,
        destination,
        destinationName: decodeURIComponent(destinationName)
      }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const templateParams = {
      name: formData.customerName,
      email: formData.email,
      message:
        `Destination: ${formData.destinationName || formData.destination}\n` +
        `Date: ${formData.date}\n` +
        `Guests: ${formData.numberOfGuests}\n` +
        `Vehicle Preference: ${formData.vehiclePreference || "No preference"}\n` +
        `Phone: ${formData.phone}\n` +
        `Additional Notes: ${formData.additionalNotes || "None"}`,
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      alert("Day trip booking submitted successfully! We'll contact you to confirm details and pricing.");
      setFormData(prev => ({
        ...prev,
        numberOfGuests: 1,
        vehiclePreference: "",
        date: "",
        additionalNotes: "",
        customerName: "",
        email: "",
        phone: ""
      }));
    } catch (error) {
      console.error("EmailJS error:", error);
      alert("Failed to submit booking. Please try again or contact us directly.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-amber-100 font-sans">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center mb-6">
            <img src="/images/araliya-tours-logo.png" alt="Araliya Tours" className="h-36 w-auto" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 font-poppins mb-4">
            Day Trip Booking
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Plan your perfect day trip with our experienced guides and comfortable vehicles.
          </p>
        </div>

        {/* Destination Preview */}
        {formData.destinationName && (
          <div className="mb-8 rounded-3xl bg-white/80 backdrop-blur border border-white/40 shadow-xl ring-1 ring-black/5 overflow-hidden">
            <div className="relative h-48">
              <img
                src={getDestinationImage(formData.destination)}
                alt={formData.destinationName}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <h2 className="text-2xl font-bold text-white mb-2">{formData.destinationName}</h2>
                <div className="inline-flex items-center rounded-full bg-white/90 px-3 py-1 text-sm font-semibold text-gray-800">
                  Victoria · Australia
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Form */}
        <div className="rounded-3xl bg-white/80 backdrop-blur border border-white/40 shadow-2xl ring-1 ring-black/5 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-6">
            <h2 className="text-2xl font-semibold text-white">Book Your Day Trip</h2>
            <p className="text-emerald-100 mt-1">Fill in the details below and we'll get back to you within 2 hours</p>
          </div>
          
          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Customer Information - MOVED TO TOP */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Customer Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Trip Details */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Trip Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Number of Guests
                  </label>
                  <input
                    type="number"
                    name="numberOfGuests"
                    value={formData.numberOfGuests}
                    onChange={handleInputChange}
                    min="1"
                    max="20"
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Vehicle Preference <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <select
                  name="vehiclePreference"
                  value={formData.vehiclePreference}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                >
                  <option value="">No preference</option>
                  {VEHICLES.map((v) => (
                    <option key={v.id} value={`${v.model} (${v.year})`}>{v.model} ({v.year}) — {v.seats} seats</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Additional Notes
                </label>
                <textarea
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Any special requirements, dietary restrictions, or additional information..."
                  className="w-full rounded-xl border border-gray-300 px-4 py-3.5 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white/80 backdrop-blur transition-all duration-200"
                />
              </div>
            </div>

            {/* Submit Section */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-4 text-white font-semibold text-lg shadow-lg hover:from-emerald-700 hover:to-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600/40 transform hover:scale-[1.02] transition-all duration-200"
              >
                <span className="flex items-center justify-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Book Day Trip
                </span>
              </button>
              
              <div className="mt-4 text-center">
                <div className="inline-flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-full">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  We'll contact you within 2 hours to confirm details and pricing
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default DayTripBookingPage;
